import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, Save, X, AlertCircle, CheckCircle, Camera, File } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { archiveService } from '../services/archive.service';
import { commissionService } from '../services/commission.service';
import Header from '../components/Header';

const EditDocument = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [commissions, setCommissions] = useState([]);
  const [loadingCommissions, setLoadingCommissions] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [originalArchive, setOriginalArchive] = useState(null);
  const [loadingArchive, setLoadingArchive] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [fileChanged, setFileChanged] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    commissionId: '',
    status: 'DRAFT',
    location: '',
    tags: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const fetchArchive = React.useCallback(async () => {
    try {
      setLoadingArchive(true);
      const data = await archiveService.getArchiveById(id);

      // Charger les membres de la commission si l'archive a une commission
      let membersArray = [];
      if (data.commission?.id) {
        try {
          const membersData = await commissionService.getCommissionMembers(data.commission.id);
          membersArray = membersData?.content || membersData || [];
        } catch (memberError) {
          console.error('Erreur lors du chargement des membres:', memberError);
        }
      }

      // Vérifier les permissions : admin, auteur, ou membre de la commission
      const isAuthor = data.createdBy?.id === user?.id;
      const isMember = membersArray.some((member) => {
        return member.user?.id === user?.id ||
               member.userId === user?.id ||
               member.id === user?.id;
      });
      const canEdit = isAdmin() || isAuthor || isMember;

      if (!canEdit) {
        setAccessDenied(true);
        return;
      }

      setOriginalArchive(data);

      // Pré-remplir le formulaire
      setFormData({
        title: data.title || '',
        description: data.description || '',
        commissionId: data.commission?.id || '',
        status: data.status || 'DRAFT',
        location: data.location || '',
        tags: data.tags ? data.tags.join(', ') : '',
      });

      // Afficher l'aperçu du fichier existant si c'est une image
      if (data.imageUrl) {
        setFilePreview(data.imageUrl);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setErrors({ general: error.message || 'Archive introuvable' });
    } finally {
      setLoadingArchive(false);
    }
  }, [id, isAdmin, user?.id]);

  const fetchCommissions = React.useCallback(async () => {
    try {
      const data = await commissionService.getActiveCommissions();
      setCommissions(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des commissions:', error);
    } finally {
      setLoadingCommissions(false);
    }
  }, []);

  useEffect(() => {
    fetchCommissions();
    fetchArchive();
  }, [fetchCommissions, fetchArchive]);

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Le titre doit contenir au moins 3 caractères';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'La description doit contenir au moins 10 caractères';
    }

    if (!formData.commissionId) {
      newErrors.commissionId = 'La commission est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    console.log('🔍 handleFileSelect - file from input:', file);
    console.log('🔍 handleFileSelect - file name:', file?.name);
    console.log('🔍 handleFileSelect - file size:', file?.size);
    if (file) {
      processFile(file);
    }
    // Reset input pour permettre de sélectionner le même fichier à nouveau
    e.target.value = '';
  };

  const processFile = (file) => {
    const allowedExtensions = ['pdf', 'doc', 'docx', 'txt', 'jpg', 'jpeg', 'png', 'gif', 'mp4', 'mp3', 'zip', 'rar'];
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();

    console.log('🔍 processFile - file received:', file);
    console.log('🔍 processFile - file.name:', file.name);
    console.log('🔍 processFile - file.size:', file.size);
    console.log('🔍 processFile - file.type:', file.type);
    console.log('🔍 processFile - file.lastModified:', new Date(file.lastModified).toISOString());

    if (!allowedExtensions.includes(fileExtension)) {
      setErrors((prev) => ({
        ...prev,
        file: `Type de fichier non supporté. Formats acceptés: ${allowedExtensions.join(', ')}`,
      }));
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        file: 'Le fichier est trop volumineux (max 50MB)',
      }));
      return;
    }

    console.log('✅ Nouveau fichier validé et accepté:', file.name, 'Taille:', file.size);
    console.log('✅ Mise à jour du state selectedFile avec:', file);
    setSelectedFile(file);
    setFileChanged(true);
    setErrors((prev) => ({ ...prev, file: '' }));

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      console.log('=== DÉBUT SOUMISSION ===');
      console.log('fileChanged:', fileChanged);
      console.log('selectedFile:', selectedFile);
      console.log('selectedFile name:', selectedFile?.name);
      console.log('selectedFile size:', selectedFile?.size);
      console.log('selectedFile type:', selectedFile?.type);
      console.log('selectedFile lastModified:', selectedFile ? new Date(selectedFile.lastModified).toISOString() : 'N/A');
      console.log('selectedFile est un fichier valide:', selectedFile && selectedFile.name && selectedFile.size >= 0);
      console.log('originalArchive.fileName:', originalArchive?.fileName);

      const formDataToSend = new FormData();

      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('description', formData.description.trim());
      formDataToSend.append('archiveDate', originalArchive.archiveDate || new Date().toISOString().split('T')[0]);

      if (formData.location.trim()) {
        formDataToSend.append('location', formData.location.trim());
      }

      formDataToSend.append('author', originalArchive.author || `${user.firstName} ${user.lastName}`);
      formDataToSend.append('commissionId', formData.commissionId);
      formDataToSend.append('status', formData.status);

      const tags = formData.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag);

      if (tags.length > 0) {
        tags.forEach((tag) => {
          formDataToSend.append('tags', tag);
        });
      }

      // Ajouter le nouveau fichier seulement si l'utilisateur a changé de fichier
      if (fileChanged && selectedFile) {
        console.log('✅ AJOUT DU NOUVEAU FICHIER AU FORMDATA');
        console.log('   Nom:', selectedFile.name);
        console.log('   Taille:', selectedFile.size, 'bytes');
        console.log('   Type:', selectedFile.type);
        console.log('   LastModified:', new Date(selectedFile.lastModified).toISOString());
        console.log('   Est un fichier valide:', selectedFile.name && selectedFile.size >= 0);
        console.log('   Objet complet selectedFile:', selectedFile);

        // Vérification critique : est-ce bien un objet File ?
        const isValidFile = selectedFile && selectedFile.name && selectedFile.size >= 0 && selectedFile.type !== undefined;
        if (!isValidFile) {
          console.error('❌ ERREUR: selectedFile n\'est pas un fichier valide!');
          console.error('   Type réel:', typeof selectedFile);
          console.error('   Constructor:', selectedFile.constructor?.name);
        }

        formDataToSend.append('file', selectedFile, selectedFile.name);
        console.log('✅ Fichier ajouté au FormData avec le nom:', selectedFile.name);
      } else {
        console.log('⚠️ PAS DE NOUVEAU FICHIER - fileChanged:', fileChanged, 'selectedFile:', !!selectedFile);
      }

      // Debug: afficher le contenu du FormData
      console.log('=== CONTENU DU FORMDATA ===');
      let hasFile = false;
      let fileDetails = null;
      for (let pair of formDataToSend.entries()) {
        if (pair[1] && typeof pair[1] === 'object' && 'name' in pair[1] && 'size' in pair[1]) {
          console.log('📎', pair[0], ':', pair[1].name, '(', pair[1].size, 'bytes)', 'Type:', pair[1].type);
          console.log('   LastModified:', new Date(pair[1].lastModified).toISOString());
          console.log('   Objet File complet:', pair[1]);
          hasFile = true;
          fileDetails = {
            name: pair[1].name,
            size: pair[1].size,
            type: pair[1].type,
            lastModified: pair[1].lastModified,
          };
        } else {
          console.log('  ', pair[0], ':', pair[1]);
        }
      }
      console.log('Fichier présent dans FormData:', hasFile);
      if (hasFile) {
        console.log('Détails du fichier dans FormData:', fileDetails);
        console.log('Est-ce le même que selectedFile ?',
          fileDetails.name === selectedFile?.name &&
          fileDetails.size === selectedFile?.size &&
          fileDetails.lastModified === selectedFile?.lastModified
        );
      }
      console.log('=== FIN CONTENU FORMDATA ===');

      console.log('Envoi au backend...');
      const response = await archiveService.updateArchive(id, formDataToSend);
      console.log('Réponse du backend:', response);

      setSuccess(true);
      setTimeout(() => {
        navigate(`/documents/${id}`);
      }, 1500);
    } catch (error) {
      console.error('❌ ERREUR lors de la modification:', error);
      console.error('Détails erreur:', JSON.stringify(error, null, 2));

      if (error.fields) {
        setErrors(error.fields);
      } else {
        alert(error.message || 'Erreur lors de la modification de l\'archive');
      }
    } finally {
      setLoading(false);
    }
  };

  const removeFile = () => {
    console.log('Suppression du nouveau fichier, retour au fichier original');
    setSelectedFile(null);
    setFileChanged(false);
    setFilePreview(originalArchive?.imageUrl || null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const getFileIcon = (file) => {
    if (!file) return <File className="w-6 h-6" />;

    const fileName = file.name.toLowerCase();

    if (fileName.endsWith('.pdf')) return '📄';
    if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) return '📝';
    if (fileName.endsWith('.txt')) return '📃';
    if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) return '📊';
    if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png') || fileName.endsWith('.gif')) return '🖼️';
    if (fileName.endsWith('.mp4')) return '🎥';
    if (fileName.endsWith('.mp3')) return '🎵';
    if (fileName.endsWith('.zip') || fileName.endsWith('.rar')) return '📦';
    return '📎';
  };

  // État de chargement
  if (loadingArchive) {
    return (
      <div>
        <Header title="Modifier l'archive" />
        <div className="px-4 md:px-8 flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  // Accès refusé
  if (accessDenied) {
    return (
      <div>
        <Header title="Accès refusé" />
        <div className="px-4 md:px-8">
          <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-xl shadow-lg p-8 text-center mt-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h2>
            <p className="text-gray-600 mb-6">
              Vous n'avez pas les permissions nécessaires pour modifier cette archive.
              Seul l'auteur ou un administrateur peut la modifier.
            </p>
            <button
              onClick={() => navigate(`/documents/${id}`)}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Retour à l'archive
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Succès
  if (success) {
    return (
      <div>
        <Header title="Modifier l'archive" />
        <div className="px-4 md:px-8">
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center mt-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Archive modifiée !</h2>
            <p className="text-gray-600 mb-6">
              L'archive a été modifiée avec succès. Redirection...
            </p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Modifier l'archive" />

      <div className="px-4 md:px-8">
        <button
          onClick={() => navigate(`/documents/${id}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Retour à l'archive</span>
        </button>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Modifier l'archive</h2>
              <p className="text-gray-600 mt-1">
                Modifiez les informations de l'archive
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Titre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre de l'archive *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Réunion Commission Culturelle - Janvier 2026"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.title}
                    </p>
                  )}
                </div>

                {/* Commission et Statut */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Commission *
                    </label>
                    <select
                      name="commissionId"
                      value={formData.commissionId}
                      onChange={handleChange}
                      disabled={loadingCommissions}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 ${
                        errors.commissionId ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Sélectionner une commission</option>
                      {commissions.map((commission) => (
                        <option key={commission.id} value={commission.id}>
                          {commission.name}
                        </option>
                      ))}
                    </select>
                    {errors.commissionId && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle size={14} />
                        {errors.commissionId}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Statut *
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="DRAFT">Brouillon</option>
                      <option value="PUBLISHED">Publié</option>
                      <option value="ARCHIVED">Archivé</option>
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Description détaillée de l'archive..."
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.description}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.description.length} caractères
                  </p>
                </div>

                {/* Lieu */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lieu (optionnel)
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Ex: Rennes, France"
                  />
                </div>

                {/* Fichier existant et nouveau */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fichier {fileChanged ? '(Nouveau fichier sélectionné ✓)' : '(Optionnel - garder l\'existant)'}
                  </label>

                  {/* Fichier actuel */}
                  {originalArchive?.fileName && !fileChanged && (
                    <div className="mb-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Fichier actuel :</p>
                      <p className="font-medium text-gray-900 text-sm">{originalArchive.fileName}</p>
                      {originalArchive.fileSize && (
                        <p className="text-xs text-gray-500 mt-1">
                          {(originalArchive.fileSize / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      )}
                    </div>
                  )}

                  {!fileChanged && (
                    <div className="space-y-3">
                      <div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.mp4,.mp3,.zip,.rar"
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-3 px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
                        >
                          <Upload className="w-6 h-6 text-gray-400" />
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-700">
                              Remplacer le fichier
                            </p>
                            <p className="text-xs text-gray-500">
                              PDF, Word, TXT, Images, Vidéo, Audio, ZIP (max 50MB)
                            </p>
                          </div>
                        </button>
                      </div>

                      <div className="md:hidden">
                        <input
                          type="file"
                          ref={cameraInputRef}
                          onChange={handleFileSelect}
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => cameraInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-3 px-4 py-4 bg-primary-50 border-2 border-primary-300 rounded-lg hover:bg-primary-100 transition-colors"
                        >
                          <Camera className="w-6 h-6 text-primary-600" />
                          <div className="text-left">
                            <p className="text-sm font-medium text-primary-700">
                              Prendre une photo
                            </p>
                            <p className="text-xs text-primary-600">
                              Utiliser l'appareil photo
                            </p>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {fileChanged && selectedFile && (
                    <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
                      <div className="flex items-start gap-4">
                        {filePreview ? (
                          <img
                            src={filePreview}
                            alt="Aperçu"
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center text-4xl">
                            {getFileIcon(selectedFile)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-green-700 font-semibold mb-1">NOUVEAU FICHIER</p>
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {selectedFile.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Annuler et garder le fichier actuel"
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                  )}

                  {errors.file && (
                    <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.file}
                    </p>
                  )}
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (séparés par des virgules)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Ex: culturel, 2026, réunion, janvier"
                  />
                </div>
              </div>

              {/* Boutons */}
              <div className="flex flex-col sm:flex-row items-center gap-4 mt-8 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium shadow-sm"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Modification en cours...</span>
                    </>
                  ) : (
                    <>
                      <Save size={20} />
                      <span className="font-medium">Enregistrer les modifications</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => navigate(`/documents/${id}`)}
                  disabled={loading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  <X size={20} />
                  <span>Annuler</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDocument;
